import React from 'react';

import {
  AlertDialog,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogFooter,
  AlertDialogContent,
} from '../ui/alert-dialog';

interface Props {
  title: string;
  description: React.ReactNode;
  onSubmit: () => Promise<void> | void;
  onCancel?: () => void;
  // Optional external state control - if not provided, manages state internally
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  variant: React.ComponentProps<typeof AlertDialogAction>['variant'];
  cancelText: string;
  submitText: string;
  loadingText?: string;
  zIndex?: number; // z-index of the dialog
}

export interface ConfirmDialogRef {
  open: () => void;
  close: () => void;
}

export const ConfirmDialog = React.forwardRef<ConfirmDialogRef, Props>(
  (
    {
      title,
      description,
      onSubmit,
      onCancel,
      open: externalOpen,
      onOpenChange: externalOnOpenChange,
      variant,
      cancelText,
      submitText,
      loadingText,
      zIndex = 50,
    },
    ref,
  ) => {
    const zIndexClass = zIndex ? `z-[${zIndex}]` : undefined;
    const [isLoading, setIsLoading] = React.useState(false);

    // Internal state management (merged from useDialogState)
    const [internalOpen, setInternalOpen] = React.useState(false);

    // Use external state if provided, otherwise use internal state
    const isOpen = externalOpen ?? internalOpen;
    const setIsOpen = externalOnOpenChange ?? setInternalOpen;

    // Expose methods via ref for programmatic control
    React.useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }));

    const handleCancel = () => {
      if (isLoading) return; // Prevent cancel during loading
      onCancel?.();
      setIsOpen(false);
    };

    const handleSubmit = async () => {
      if (isLoading) return; // Prevent double submission

      setIsLoading(true);
      try {
        await onSubmit();
        setIsOpen(false); // Close dialog after successful submission
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent zIndex={zIndexClass}>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription>{description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel} disabled={isLoading}>
              {cancelText}
            </AlertDialogCancel>
            <AlertDialogAction variant={variant} onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? loadingText || submitText : submitText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);

ConfirmDialog.displayName = 'ConfirmDialog';
